import type { AssetGroup, AssetType } from "@phc/common/gen/preferencesGen"
import { IndustryType } from "@phc/common/gen/preferencesGen"
import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react"
import { getLocationId } from "../../../../utils/helpers"
import { isAdminBoundaryFromPlaceType } from "../../../../utils/helpers/assetHelper"
import { trackEvent } from "../../../../utils/mixpanel"
import type { Asset } from "../../../../utils/preferencesApi"
import { toAssetInput } from "../../../../utils/preferencesApi"
import { MIXED_TEXT } from "../EditAssetsDialog"
import { useUpdateAssets } from "./useUpdateAsset"

interface BulkUpdateAssetsProps {
  saveAssets: () => void
  assetsByGroupId: Map<string, Set<Asset>>
  ubiquitousAssetGroups: AssetGroup[]
  selectedGroups?: AssetGroup[]
  setSelectedGroups: Dispatch<SetStateAction<AssetGroup[] | undefined>>
  selectedIndustryType?: IndustryType
  setSelectedIndustryType: Dispatch<SetStateAction<IndustryType | undefined>>
  selectedAssetType?: AssetType
  setSelectedAssetType: Dispatch<SetStateAction<AssetType | undefined>>
  selectedThreatRadius?: number
  setSelectedThreatRadius: Dispatch<SetStateAction<number | undefined>>
  updateIsPending: boolean
}

export const useBulkUpdateAssets = (assets: Asset[]): BulkUpdateAssetsProps => {
  const { mutate: updateAssets, isPending } = useUpdateAssets()

  const [selectedIndustryType, setSelectedIndustryType] =
    useState<IndustryType>()
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType>()
  const [selectedThreatRadius, setSelectedThreatRadius] = useState<number>()

  const assetGroupsById = useMemo(() => {
    const byGroupId = new Map<string, AssetGroup>()
    assets.forEach(asset => {
      ;(asset.asset_groups ?? []).forEach(group => {
        if (group.asset_group_id) {
          byGroupId.set(group.asset_group_id, group)
        }
      })
    })
    return byGroupId
  }, [assets])

  // If every asset has the same group assigned to it, display that group in its own pill
  const assetsByGroupId = useMemo(() => {
    const byGroupId = new Map<string, Set<Asset>>()
    assets.forEach(asset => {
      ;(asset.asset_groups ?? []).forEach(group => {
        if (!group.asset_group_id) return
        const groupIdAssets = byGroupId.get(group.asset_group_id) || new Set()
        byGroupId.set(group.asset_group_id, groupIdAssets.add(asset))
      })
    })
    return byGroupId
  }, [assets])

  // If an asset group id has the same number of assets as assets.length, then every asset
  // has that group assigned to it.
  const ubiquitousAssetGroups = useMemo(() => {
    return Array.from(assetsByGroupId.entries())
      .filter(entry => {
        const groupAssets = entry[1]
        return groupAssets.size === assets.length
      })
      .flatMap(entry => assetGroupsById.get(entry[0]) ?? [])
  }, [assetGroupsById, assets.length, assetsByGroupId])

  const [selectedGroups, setSelectedGroups] = useState<
    AssetGroup[] | undefined
  >(ubiquitousAssetGroups)

  const selectedGroupIdSet = useMemo(
    () =>
      selectedGroups?.reduce((idSet, group) => {
        if (group.asset_group_id) {
          idSet.add(group.asset_group_id)
        }
        return idSet
      }, new Set<string>()),
    [selectedGroups]
  )

  const ubiquitousAssetGroupIdSet = useMemo(
    () =>
      ubiquitousAssetGroups.reduce((idSet, group) => {
        if (group.asset_group_id) {
          idSet.add(group.asset_group_id)
        }
        return idSet
      }, new Set<string>()),
    [ubiquitousAssetGroups]
  )

  const saveAssets = useCallback(() => {
    const updatedAssets = assets.map(asset => {
      const isAdminBoundary = isAdminBoundaryFromPlaceType(
        asset.base_event?.mapbox_location?.place_type ?? undefined
      )

      const updatedGroups = getUpdatedGroupsForAsset(
        asset,
        ubiquitousAssetGroupIdSet,
        selectedGroupIdSet,
        selectedGroups
      )

      const industry =
        selectedIndustryType ??
        asset.industry ??
        IndustryType.INDUSTRY_TYPE_UNSPECIFIED
      trackEvent("MANAGE_LOCATION", {
        action: "edit",
        locationId: getLocationId(asset),
        industry,
      })

      // Admin boundaries can't have industries, threat radii, or asset types
      if (isAdminBoundary) {
        return {
          ...asset,
          asset_groups: updatedGroups,
        }
      }

      return {
        ...asset,
        industry,
        threat_radius:
          // number can be 0, so check for undefined to see if we've changed it
          selectedThreatRadius !== undefined
            ? selectedThreatRadius
            : asset.threat_radius,
        asset_groups: updatedGroups,
        asset_types: selectedAssetType
          ? [selectedAssetType]
          : (asset.asset_types ?? []),
      }
    })

    updateAssets({
      assets: updatedAssets.map(asset => toAssetInput(asset)),
    })
  }, [
    assets,
    selectedAssetType,
    selectedGroupIdSet,
    selectedGroups,
    selectedIndustryType,
    selectedThreatRadius,
    ubiquitousAssetGroupIdSet,
    updateAssets,
  ])
  return {
    saveAssets,
    assetsByGroupId,
    ubiquitousAssetGroups,
    selectedGroups,
    setSelectedGroups,
    selectedIndustryType,
    setSelectedIndustryType,
    selectedAssetType,
    setSelectedAssetType,
    selectedThreatRadius,
    setSelectedThreatRadius,
    updateIsPending: isPending,
  }
}

function getUpdatedGroupsForAsset(
  asset: Asset,
  ubiquitousAssetGroupIdSet: Set<string>,
  selectedGroupIdSet?: Set<string>,
  selectedGroups?: AssetGroup[]
) {
  // Filter out the mixed text item which is used as a placeholder
  const selectedWithMixedFilteredOut =
    selectedGroups?.filter(g => g.asset_group_id !== MIXED_TEXT) || []

  // Add the selected groups to the exising asset groups
  const assetGroupsWithSelected = (asset.asset_groups ?? []).concat(
    selectedWithMixedFilteredOut
  )

  // If an existing asset group is in the ubiquitous set but is not in the selected set,
  // we need to remove it from the asset's group list since that means it was removed
  return assetGroupsWithSelected.filter(
    g =>
      // if the current asset group is in the ubiquitous set but is not in the selected set, remove it
      // if the current asset group is in the ubiquitous set and the selected set, keep it
      // if the current asset group is not in the ubiquitious set and is in the selected set, keep it
      // if the current asset group is not in the ubiquitious set and is not in the selected set, still keep
      !(
        g.asset_group_id &&
        ubiquitousAssetGroupIdSet.has(g.asset_group_id) &&
        !selectedGroupIdSet?.has(g.asset_group_id)
      )
  )
}
